import React, { useState } from 'react';
import './TermsAndConditions.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useLogout, useUserData, useScrollToTop } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Issue #118: Terms and Conditions Page (AGB) - Authenticated version
 * For logged-in users accessing from Profile page
 * Note: AGB is only available in German as per legal requirements
 */
export function AuthenticatedTermsAndConditions() {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  return (
    <TermsAndConditionsContent
      t={t}
      isAuthenticated={true}
      onNavigateBack={navigation.onNavigateBack}
      userData={userData}
      handleLogout={handleLogout}
      isLoggingOut={isLoggingOut}
      logoutError={logoutError}
      clearLogoutError={clearLogoutError}
      showCreateOptions={showCreateOptions}
      setShowCreateOptions={setShowCreateOptions}
    />
  );
}

/**
 * Issue #118: Terms and Conditions Page (AGB) - Unauthenticated version
 * For users accessing from Register page before creating account
 */
function TermsAndConditions({ onNavigateBack }) {
  const { t } = useLanguage();

  return (
    <TermsAndConditionsContent
      t={t}
      isAuthenticated={false}
      onNavigateBack={onNavigateBack}
    />
  );
}

/**
 * Shared content component for Terms and Conditions
 */
function TermsAndConditionsContent({
  t,
  isAuthenticated,
  onNavigateBack,
  userData,
  handleLogout,
  isLoggingOut,
  logoutError,
  clearLogoutError,
  showCreateOptions,
  setShowCreateOptions
}) {
  // Issue #374: Always start the document from the top, regardless of the
  // scroll position of the previously-displayed screen.
  useScrollToTop();

  // Terms and Conditions content (AGB) - German only as per legal requirements
  // Note: This document is only available in German / Dieses Dokument ist nur auf Deutsch verfügbar
  const termsContent = `================================================================================
                    ALLGEMEINE GESCHÄFTSBEDINGUNGEN (AGB)
                              UNIVERSE App
================================================================================

                    Stand: 28. Januar 2026
                    Version: 1.1

================================================================================
                         INHALTSVERZEICHNIS
================================================================================

1.  Geltungsbereich und Vertragsgegenstand
2.  Registrierung und Vertragsschluss
3.  Leistungsbeschreibung
4.  Pflichten der Nutzer
5.  Nutzerinhalte und Rechteeinräumung
6.  Marktplatz
7.  Kursbewertungen
8.  Anonyme Beiträge
9.  Chat-Funktion
10. Melde- und Beschwerdeverfahren
11. Haftung
12. Laufzeit und Kündigung
13. Änderungen der AGB
14. Datenschutz
15. Schlussbestimmungen

================================================================================
                        1. GELTUNGSBEREICH UND VERTRAGSGEGENSTAND
================================================================================

1.1 Betreiber und Anwendungsbereich

Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") regeln die
Nutzung der UNIVERSE App (nachfolgend "App" oder "Plattform"), die als
Web-Anwendung und mobile Anwendung für Android und iOS verfügbar ist.

Betreiber der Plattform ist:
[GAIA Software UG (haftungsbeschränkt)]
[Gerhardstraße 6]
[22926 Ahrensburg]
Deutschland

E-Mail: [kontakt@universe-app.online]

Handelsregister: [Amtsgericht Lübeck, HRB-27728]
Geschäftsführer: [Jihyun Lee, Julius von Sulecki]

1.2 Zielgruppe

Die Plattform richtet sich ausschließlich an Studierende, die an einer
deutschen Hochschule eingeschrieben sind und über eine gültige
universitäre E-Mail-Adresse verfügen. Die Nutzung ist auf das Gebiet
der Bundesrepublik Deutschland beschränkt.

1.3 Geltung der AGB

Diese AGB gelten für alle Verträge zwischen dem Betreiber und den
Nutzern über die Nutzung der Plattform. Abweichende Bedingungen des
Nutzers finden keine Anwendung, es sei denn, der Betreiber stimmt ihrer
Geltung ausdrücklich schriftlich zu.

1.4 Änderungen der AGB

Der Betreiber behält sich vor, diese AGB mit Wirkung für die Zukunft
zu ändern, soweit dies unter Berücksichtigung der Interessen des
Betreibers für den Nutzer zumutbar ist. Über Änderungen wird der
Nutzer mindestens 30 Tage vor Inkrafttreten per E-Mail informiert.
Widerspricht der Nutzer nicht innerhalb von 30 Tagen nach Zugang der
Änderungsmitteilung, gelten die geänderten AGB als angenommen. Der
Nutzer wird in der Änderungsmitteilung auf diese Rechtsfolge
hingewiesen. Bei Widerspruch steht beiden Parteien ein Kündigungsrecht zu.

================================================================================
                        2. REGISTRIERUNG UND VERTRAGSSCHLUSS
================================================================================

2.1 Registrierungsvoraussetzungen

Zur Nutzung der Plattform ist eine Registrierung erforderlich. Zur
Registrierung berechtigt sind ausschließlich:

a) Natürliche Personen, die das 16. Lebensjahr vollendet haben
b) Personen, die an einer deutschen Hochschule eingeschrieben sind
c) Personen, die über eine gültige universitäre E-Mail-Adresse
   (Domain einer deutschen Hochschule) verfügen

2.2 Registrierungsprozess

Die Registrierung erfolgt durch:

a) Eingabe des Vornamens und Nachnamens
b) Eingabe einer gültigen universitären E-Mail-Adresse
c) Festlegung eines Passworts gemäß den Sicherheitsanforderungen
d) Bestätigung der Kenntnisnahme dieser AGB durch aktives Anklicken
   eines nicht vorausgefüllten Kontrollkästchens
e) Bestätigung der Kenntnisnahme der Datenschutzerklärung durch
   separates aktives Anklicken eines nicht vorausgefüllten
   Kontrollkästchens
f) Absenden des Registrierungsformulars

2.3 E-Mail-Verifizierung

Nach Absenden des Registrierungsformulars erhält der Nutzer eine
Bestätigungs-E-Mail an die angegebene universitäre E-Mail-Adresse.
Der Nutzungsvertrag kommt erst mit erfolgreicher Verifizierung der
E-Mail-Adresse durch Klick auf den Bestätigungslink zustande.

2.4 Konto und Zugangsdaten

Der Nutzer ist verpflichtet:

a) Bei der Registrierung wahrheitsgetreue und vollständige Angaben
   zu machen
b) Seine Zugangsdaten geheim zu halten und vor dem Zugriff Dritter
   zu schützen
c) Den Betreiber unverzüglich zu informieren, wenn er Kenntnis
   davon erlangt, dass Dritte seine Zugangsdaten missbrauchen
d) Sein Konto ausschließlich persönlich zu nutzen

2.5 Mehrfachkonten

Jeder Nutzer darf nur ein Konto anlegen. Die Erstellung mehrerer
Konten ist untersagt und berechtigt den Betreiber zur Sperrung
sämtlicher Konten des Nutzers.

================================================================================
                        3. LEISTUNGSBESCHREIBUNG
================================================================================

3.1 Grundlegende Dienste

Die Plattform stellt Studierenden deutscher Hochschulen folgende
kostenlose Dienste zur Verfügung:

a) Beiträge (Posts): Erstellung, Bearbeitung und Löschung von
   Textbeiträgen mit optionalen Bildern, sichtbar für Studierende
   derselben Hochschule
b) Marktplatz: Erstellung von Angeboten zum Verkauf oder zur
   kostenlosen Abgabe von Gegenständen
c) Chat: Private Kommunikation zwischen Nutzern, insbesondere im
   Rahmen von Marktplatztransaktionen
d) Kursbewertungen: Bewertung von Lehrveranstaltungen der eigenen
   Hochschule
e) Benachrichtigungen: Information über relevante Aktivitäten
   (Kommentare, Likes, Nachrichten)

3.2 Verfügbarkeit

Der Betreiber bemüht sich um eine hohe Verfügbarkeit der Plattform,
garantiert jedoch keine ununterbrochene Erreichbarkeit. Wartungsarbeiten
werden nach Möglichkeit angekündigt. Der Betreiber haftet nicht für
Nichtverfügbarkeit aufgrund von Umständen außerhalb seines
Einflussbereichs (z.B. höhere Gewalt, Störungen im Internet).

3.3 Weiterentwicklung

Der Betreiber ist berechtigt, die angebotenen Dienste jederzeit zu
erweitern, zu ändern oder einzustellen, soweit dies für den Nutzer
zumutbar ist. Wesentliche Änderungen werden mit angemessener Frist
angekündigt.

================================================================================
                        4. PFLICHTEN DER NUTZER
================================================================================

4.1 Allgemeine Verhaltenspflichten

Der Nutzer verpflichtet sich:

a) Die Plattform nur zu rechtmäßigen Zwecken zu nutzen
b) Alle anwendbaren Gesetze und Vorschriften einzuhalten
c) Die Rechte anderer Nutzer und Dritter zu respektieren
d) Keine falschen oder irreführenden Angaben zu machen

4.2 Verbotene Inhalte

Es ist untersagt, Inhalte zu veröffentlichen, die:

a) Gegen geltendes Recht verstoßen, insbesondere strafbare Inhalte
b) Die Rechte Dritter verletzen (Urheberrechte, Markenrechte,
   Persönlichkeitsrechte)
c) Beleidigend, bedrohend, diskriminierend oder menschenverachtend sind
d) Gewalt verherrlichen oder zu Gewalt aufrufen
e) Pornografische oder jugendgefährdende Inhalte darstellen
f) Falsche Tatsachenbehauptungen enthalten
g) Spam, Werbung oder kommerzielle Inhalte ohne Genehmigung enthalten
h) Schadsoftware oder Links zu schädlichen Inhalten enthalten
i) Die Privatsphäre anderer Nutzer verletzen (z.B. Veröffentlichung
   persönlicher Daten ohne Einwilligung)
j) Zur Umgehung der Plattformregeln geeignet sind

4.3 Verbotene Verhaltensweisen

Es ist untersagt:

a) Die Plattform zu kommerziellen Zwecken zu nutzen (außer über den
   Marktplatz für private Verkäufe)
b) Die Identität anderer Personen anzunehmen oder sich als andere
   Person auszugeben
c) Automatisierte Zugriffe auf die Plattform vorzunehmen (Bots, Scraper)
d) Sicherheitsmechanismen der Plattform zu umgehen oder zu stören
e) Die Infrastruktur der Plattform übermäßig zu belasten
f) Andere Nutzer zu belästigen oder zu stalken

4.4 Meldepflicht

Der Nutzer ist verpflichtet, erkannte Verstöße gegen diese AGB oder
rechtswidrige Inhalte unverzüglich über die Meldefunktion der
Plattform anzuzeigen.

================================================================================
                        5. NUTZERINHALTE UND RECHTEEINRÄUMUNG
================================================================================

5.1 Eigentum an Nutzerinhalten

Der Nutzer behält sämtliche Urheberrechte und sonstigen Rechte an den
von ihm erstellten Inhalten (nachfolgend "Nutzerinhalte"). Nutzerinhalte
umfassen insbesondere Beiträge, Kommentare, Bilder, Marktplatzanzeigen,
Kursbewertungen und Chat-Nachrichten.

5.2 Lizenzeinräumung an den Betreiber

Mit dem Hochladen oder Erstellen von Nutzerinhalten räumt der Nutzer
dem Betreiber das nicht-ausschließliche, übertragbare,
unterlizenzierbare, zeitlich und räumlich unbeschränkte Recht ein,
die Nutzerinhalte im Rahmen des Betriebs der Plattform zu nutzen,
insbesondere:

a) Zu speichern und zu vervielfältigen
b) Anderen Nutzern anzuzeigen und zugänglich zu machen
c) Technisch zu bearbeiten (z.B. Formatanpassung, Komprimierung)
d) Im Rahmen von Sicherungskopien zu vervielfältigen

Diese Lizenz dient ausschließlich dem technischen Betrieb der Plattform
und endet mit Löschung des Nutzerinhalts durch den Nutzer, soweit
keine gesetzlichen Aufbewahrungspflichten bestehen.

5.3 Gewährleistungen des Nutzers

Der Nutzer gewährleistet, dass:

a) Er berechtigt ist, die Nutzerinhalte zu erstellen und die Rechte
   gemäß Ziffer 5.2 einzuräumen
b) Die Nutzerinhalte keine Rechte Dritter verletzen
c) Die Nutzerinhalte nicht gegen geltendes Recht verstoßen

5.4 Freistellung

Der Nutzer stellt den Betreiber von sämtlichen Ansprüchen Dritter
frei, die diese aufgrund einer Verletzung ihrer Rechte durch
Nutzerinhalte geltend machen. Der Nutzer übernimmt die Kosten einer
angemessenen Rechtsverteidigung des Betreibers. Dies gilt nicht,
soweit der Nutzer die Rechtsverletzung nicht zu vertreten hat.

5.5 Recht zur Entfernung

Der Betreiber ist berechtigt, Nutzerinhalte zu entfernen oder deren
Sichtbarkeit einzuschränken, wenn:

a) Ein Verstoß gegen diese AGB vorliegt oder vermutet wird
b) Eine Verletzung von Rechten Dritter vorliegt oder vermutet wird
c) Eine behördliche oder gerichtliche Anordnung dies erfordert
d) Dies aus technischen Gründen erforderlich ist

Der Nutzer wird über Maßnahmen gemäß Ziffer 10 informiert.

================================================================================
                        6. MARKTPLATZ
================================================================================

6.1 Funktion des Marktplatzes

Der Marktplatz ermöglicht Nutzern, Gegenstände anderen Nutzern zum
Kauf oder zur kostenlosen Abgabe anzubieten. Der Betreiber stellt
lediglich die technische Infrastruktur bereit und ist nicht selbst
Vertragspartei der zwischen Nutzern geschlossenen Verträge.

6.2 Angebote erstellen

Der Nutzer kann Angebote mit folgenden Informationen erstellen:

a) Titel und Beschreibung des Gegenstands
b) Bilder des Gegenstands
c) Preis oder Kennzeichnung als kostenlose Abgabe
d) Standort (optional)

6.3 Verbotene Angebote

Es ist untersagt, folgende Gegenstände anzubieten:

a) Gegenstände, deren Verkauf gesetzlich verboten ist
b) Gestohlene oder illegal erworbene Gegenstände
c) Waffen, Munition oder explosive Stoffe
d) Drogen, Betäubungsmittel oder deren Zubehör
e) Rezeptpflichtige Medikamente
f) Jugendgefährdende Medien
g) Fälschungen oder Produktpiraterie
h) Lebende Tiere
i) Dienstleistungen oder gewerbliche Angebote
j) Gegenstände, die Rechte Dritter verletzen

6.4 Vertragsschluss zwischen Nutzern

Der Kauf- oder Schenkungsvertrag kommt ausschließlich zwischen den
beteiligten Nutzern zustande. Der Betreiber ist nicht Vertragspartei
und übernimmt keine Gewährleistung für die angebotenen Gegenstände
oder deren Beschreibung.

6.5 Pflichten des Anbieters

Der Anbieter verpflichtet sich:

a) Die Gegenstände wahrheitsgemäß und vollständig zu beschreiben
b) Nur Gegenstände anzubieten, zu deren Verkauf er berechtigt ist
c) Mängel oder Beschädigungen anzugeben
d) Verkaufte Gegenstände als verkauft zu markieren oder das Angebot
   zu entfernen

6.6 Keine Gebühren

Die Nutzung des Marktplatzes ist derzeit kostenlos. Der Betreiber
behält sich vor, künftig Gebühren einzuführen, worüber Nutzer
rechtzeitig informiert werden.

================================================================================
                        7. KURSBEWERTUNGEN
================================================================================

7.1 Zweck der Kursbewertungen

Die Funktion ermöglicht Studierenden, Lehrveranstaltungen ihrer
Hochschule zu bewerten, um anderen Studierenden bei der Kursauswahl
zu helfen.

7.2 Bewertungsberechtigung

Zur Abgabe einer Bewertung ist berechtigt, wer:

a) An der entsprechenden Hochschule eingeschrieben ist
b) Den zu bewertenden Kurs besucht hat oder besucht

7.3 Inhalt der Bewertungen

Bewertungen müssen:

a) Auf eigenem Erleben basieren
b) Sachlich und fair formuliert sein
c) Tatsachen von Meinungen unterscheidbar machen
d) Die Persönlichkeitsrechte der Dozierenden wahren

7.4 Verbotene Bewertungsinhalte

Es ist untersagt:

a) Falsche Tatsachenbehauptungen aufzustellen
b) Beleidigende oder verleumderische Aussagen zu machen
c) Bewertungen für nicht besuchte Kurse abzugeben
d) Bewertungen im Auftrag Dritter abzugeben
e) Bewertungen zu manipulieren (z.B. Mehrfachbewertungen)

7.5 Haftung für Bewertungen

Der Nutzer haftet für den Inhalt seiner Bewertungen. Der Betreiber
überprüft Bewertungen nicht proaktiv, entfernt jedoch Bewertungen
nach Kenntniserlangung, die gegen diese AGB verstoßen.

================================================================================
                        8. ANONYME BEITRÄGE
================================================================================

8.1 Möglichkeit anonymer Beiträge

Die Plattform ermöglicht Nutzern, Beiträge und Kommentare anonym zu
veröffentlichen. Bei anonymen Beiträgen wird anderen Nutzern lediglich
"Anonym" anstelle des Nutzernamens angezeigt.

8.2 Umfang der Anonymität

Die Anonymität schützt die Identität des Nutzers gegenüber anderen
Nutzern der Plattform. Der Betreiber speichert jedoch auch bei anonymen
Beiträgen technische Daten (z.B. IP-Adresse, Konto-ID), die eine
Identifizierung ermöglichen.

8.3 Grenzen der Anonymität

Die Anonymität schützt nicht vor:

a) Auskunftsersuchen von Strafverfolgungsbehörden
b) Gerichtlichen Anordnungen zur Offenlegung
c) Der Durchsetzung von Rechten Dritter bei rechtswidrigen Inhalten

Der Betreiber ist berechtigt und gegebenenfalls verpflichtet, die
Identität des Nutzers auf behördliche oder gerichtliche Anordnung
offenzulegen.

8.4 Geltung der AGB

Die anonyme Nutzung entbindet nicht von der Einhaltung dieser AGB.
Insbesondere gelten die Verbote gemäß Ziffer 4 unverändert.

================================================================================
                        9. CHAT-FUNKTION
================================================================================

9.1 Zweck der Chat-Funktion

Die Chat-Funktion ermöglicht die private Kommunikation zwischen
Nutzern, insbesondere zur Abwicklung von Marktplatztransaktionen.

9.2 Vertraulichkeit

Chat-Nachrichten sind nur für die beteiligten Nutzer sichtbar. Der
Betreiber greift auf Chat-Inhalte nur zu, wenn:

a) Dies zur technischen Bereitstellung des Dienstes erforderlich ist
b) Ein begründeter Verdacht auf Rechtsverstoß besteht
c) Eine behördliche oder gerichtliche Anordnung vorliegt

9.3 Speicherung

Chat-Nachrichten werden für die Dauer des Bestehens der Konten der
beteiligten Nutzer gespeichert. Näheres regelt die Datenschutzerklärung.

9.4 Verbotene Inhalte

Die Regelungen zu verbotenen Inhalten gemäß Ziffer 4.2 gelten für
Chat-Nachrichten entsprechend. Insbesondere ist es untersagt, andere
Nutzer über den Chat zu belästigen, zu bedrohen oder zu beleidigen.

9.5 Blockieren von Nutzern

Der Nutzer kann andere Nutzer blockieren, um keine weiteren
Nachrichten von diesen zu erhalten. Bei Blockierung:

a) Wird die Konversation aus der Nachrichtenübersicht des
   blockierenden Nutzers entfernt
b) Kann der blockierte Nutzer weiterhin Nachrichten senden, diese
   werden jedoch nicht an den blockierenden Nutzer zugestellt
c) Wird der blockierte Nutzer nicht über die Blockierung informiert
d) Kann der blockierende Nutzer die Blockierung jederzeit aufheben

Das Blockieren entbindet nicht von der Einhaltung dieser AGB.
Missbräuchliches Blockieren (z.B. zur Vermeidung berechtigter
Reklamationen bei Marktplatztransaktionen) kann als Verstoß
gewertet werden.

9.6 Benachrichtigungseinstellungen für einzelne Chats

Der Nutzer kann Benachrichtigungen für einzelne Chat-Konversationen
deaktivieren. In diesem Fall:

a) Werden keine Push-Benachrichtigungen für neue Nachrichten gesendet
b) Werden keine Benachrichtigungen in der Benachrichtigungsübersicht
   angezeigt
c) Bleibt die Anzahl ungelesener Nachrichten im Chat sichtbar
d) Kann die Einstellung jederzeit wieder aktiviert werden

================================================================================
                        10. MELDE- UND BESCHWERDEVERFAHREN
================================================================================

10.1 Meldung von Inhalten

Jeder Nutzer kann Inhalte, die gegen diese AGB oder geltendes Recht
verstoßen, über die Meldefunktion der Plattform melden. Die Meldung
sollte enthalten:

a) Art des Verstoßes
b) Begründung der Meldung
c) Falls zutreffend: Nachweise für den Verstoß

10.2 Bearbeitung von Meldungen

Der Betreiber bearbeitet Meldungen zeitnah und objektiv. Er:

a) Prüft den gemeldeten Inhalt
b) Ergreift gegebenenfalls Maßnahmen (Entfernung, Einschränkung,
   Verwarnung, Kontosperre)
c) Informiert den meldenden Nutzer über das Ergebnis
d) Informiert den betroffenen Nutzer über ergriffene Maßnahmen
   und deren Begründung

10.2a Automatische Inhaltsmoderation

Die Plattform verwendet technische Systeme zur automatischen
Erkennung von unangemessenen Inhalten:

a) Wortfilter: Inhalte mit unangemessenen Wörtern oder Ausdrücken
   können nicht hochgeladen werden. Der Nutzer wird informiert,
   welche Wörter den Upload verhindern
b) Automatische Löschung: Inhalte, die von 5 oder mehr verschiedenen
   Nutzern gemeldet werden, werden automatisch entfernt
c) Benachrichtigung: Bei automatischer Löschung wird der Nutzer per
   E-Mail über die Entfernung und den Grund informiert
d) Wiederherstellung: Automatisch gelöschte Inhalte können auf
   Antrag vom Betreiber überprüft und wiederhergestellt werden

Die automatischen Systeme unterstützen die Moderation, ersetzen
jedoch keine menschliche Prüfung bei Beschwerden.

10.3 Beschwerde gegen Maßnahmen

Nutzer, gegen die Maßnahmen ergriffen wurden, können innerhalb von
14 Tagen Beschwerde einlegen. Die Beschwerde ist zu begründen. Der
Betreiber prüft die Beschwerde und teilt das Ergebnis mit.

10.4 Dokumentation

Der Betreiber dokumentiert eingegangene Meldungen, ergriffene
Maßnahmen und Beschwerden zu Transparenzzwecken.

================================================================================
                        11. HAFTUNG
================================================================================

11.1 Haftung des Betreibers

Die Haftung des Betreibers bestimmt sich nach folgenden Grundsätzen:

a) Für Vorsatz und grobe Fahrlässigkeit haftet der Betreiber
   unbeschränkt.

b) Für leichte Fahrlässigkeit haftet der Betreiber nur bei Verletzung
   wesentlicher Vertragspflichten (Kardinalpflichten). Kardinalpflichten
   sind solche Pflichten, deren Erfüllung die ordnungsgemäße
   Durchführung des Vertrages überhaupt erst ermöglicht und auf deren
   Einhaltung der Nutzer regelmäßig vertrauen darf. In diesen Fällen
   ist die Haftung auf den typischen, vorhersehbaren Schaden beschränkt.

c) Die Haftung für Schäden aus der Verletzung des Lebens, des Körpers
   oder der Gesundheit bleibt unbeschränkt.

d) Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.

11.2 Haftung für Nutzerinhalte

Der Betreiber haftet nicht für Nutzerinhalte. Eine Überwachungspflicht
besteht nicht. Nach Kenntniserlangung von rechtswidrigen Inhalten
entfernt der Betreiber diese unverzüglich.

11.3 Haftung für Marktplatztransaktionen

Der Betreiber haftet nicht für:

a) Die Richtigkeit von Angebotsbeschreibungen
b) Die Qualität oder Mängelfreiheit angebotener Gegenstände
c) Die Erfüllung von Verträgen zwischen Nutzern
d) Schäden aus Transaktionen zwischen Nutzern

11.4 Haftung für Drittinhalte

Der Betreiber haftet nicht für Inhalte verlinkter externer Websites.
Für diese Inhalte ist ausschließlich der jeweilige Anbieter
verantwortlich.

11.5 Erfüllungsgehilfen

Die vorstehenden Haftungsbeschränkungen gelten auch zugunsten der
gesetzlichen Vertreter, Angestellten und Erfüllungsgehilfen des
Betreibers.

================================================================================
                        12. LAUFZEIT UND KÜNDIGUNG
================================================================================

12.1 Vertragsbeginn und -laufzeit

Der Nutzungsvertrag beginnt mit erfolgreicher E-Mail-Verifizierung
gemäß Ziffer 2.3 und wird auf unbestimmte Zeit geschlossen.

12.2 Kündigung durch den Nutzer

Der Nutzer kann den Vertrag jederzeit ohne Angabe von Gründen
kündigen. Die Kündigung erfolgt durch Löschung des Kontos in den
Profileinstellungen oder durch Mitteilung an den Betreiber.

12.2a Kontolöschung mit Wiederherstellungsfrist

Bei Beantragung der Kontolöschung durch den Nutzer gilt eine
30-tägige Wiederherstellungsfrist:

a) Der Nutzer kann innerhalb von 30 Tagen nach Löschantrag sein
   Konto durch erneuten Login wiederherstellen
b) Während der Wiederherstellungsfrist werden die Kontodaten
   gespeichert, aber das Konto ist nicht aktiv
c) Nach Ablauf der 30 Tage werden personenbezogene Daten und
   Marktplatzangebote unwiderruflich gelöscht
d) Beiträge, Kommentare und Kursbewertungen werden anonymisiert
   und bleiben erhalten
e) Der Nutzer wird bei Löschantrag über diese Regelung informiert

12.3 Kündigung durch den Betreiber

Der Betreiber kann den Vertrag mit einer Frist von 30 Tagen kündigen.
Die Kündigung erfolgt per E-Mail unter Angabe der Gründe.

12.4 Außerordentliche Kündigung

Beide Parteien sind zur außerordentlichen Kündigung aus wichtigem
Grund berechtigt. Ein wichtiger Grund liegt für den Betreiber
insbesondere vor bei:

a) Schwerwiegenden oder wiederholten Verstößen gegen diese AGB
b) Vorliegen von Straftaten im Zusammenhang mit der Plattformnutzung
c) Bereitstellung falscher Identitätsinformationen
d) Missbrauch der Plattform entgegen ihrem Zweck

Bei außerordentlicher Kündigung erfolgt eine sofortige Sperrung des
Kontos. Der Nutzer wird unter Angabe der Gründe informiert.

12.5 Löschung wegen Inaktivität

Der Betreiber ist berechtigt, Konten nach 12 Monaten durchgehender
Inaktivität (kein Login) zu löschen. Der Nutzer wird:

a) 30 Tage vor der geplanten Löschung per E-Mail gewarnt
b) Über die Möglichkeit informiert, durch Login die Löschung
   abzuwenden

12.6 Folgen der Beendigung

Bei Beendigung des Vertrages:

a) Wird das Konto deaktiviert und nach Ablauf gesetzlicher
   Aufbewahrungsfristen gelöscht
b) Werden Marktplatzanzeigen entfernt
c) Bleiben Beiträge und Bewertungen in anonymisierter Form erhalten,
   soweit der Nutzer nicht deren Löschung verlangt
d) Erlöschen die Lizenzrechte gemäß Ziffer 5.2, soweit nicht für
   bereits veröffentlichte Inhalte erforderlich

================================================================================
                        13. ÄNDERUNGEN DER AGB
================================================================================

13.1 Änderungsrecht

Der Betreiber ist berechtigt, diese AGB zu ändern, wenn:

a) Dies aufgrund geänderter Rechtsvorschriften erforderlich ist
b) Dies aufgrund von Rechtsprechung erforderlich ist
c) Neue Funktionen eingeführt werden
d) Dies zur Schließung von Regelungslücken erforderlich ist

Änderungen erfolgen nur, soweit sie für den Nutzer zumutbar sind.

13.2 Benachrichtigung

Über Änderungen informiert der Betreiber den Nutzer mindestens
30 Tage vor deren Inkrafttreten per E-Mail. Die Mitteilung enthält:

a) Den vollständigen Text der geänderten AGB
b) Eine Gegenüberstellung der wesentlichen Änderungen
c) Das Datum des Inkrafttretens
d) Den Hinweis auf das Widerspruchsrecht und dessen Folgen

13.3 Zustimmung zu Änderungen

Widerspricht der Nutzer den geänderten AGB nicht innerhalb von 30
Tagen nach Zugang der Mitteilung, gelten die geänderten AGB als
angenommen. Bei Widerspruch kann der Betreiber das Vertragsverhältnis
mit Wirkung zum Zeitpunkt des Inkrafttretens der Änderung kündigen.

================================================================================
                        14. DATENSCHUTZ
================================================================================

14.1 Datenschutzerklärung

Die Erhebung und Verarbeitung personenbezogener Daten erfolgt gemäß
der separaten Datenschutzerklärung, die unter [URL der
Datenschutzerklärung] abrufbar ist.

14.2 Datenverarbeitung

Die Datenschutzerklärung informiert insbesondere über:

a) Art und Umfang der erhobenen Daten
b) Zwecke der Datenverarbeitung
c) Rechtsgrundlagen der Verarbeitung
d) Empfänger der Daten
e) Speicherdauer
f) Rechte der betroffenen Personen

14.3 Einwilligung

Soweit die Datenverarbeitung auf der Einwilligung des Nutzers beruht,
kann diese jederzeit mit Wirkung für die Zukunft widerrufen werden.

================================================================================
                        15. SCHLUSSBESTIMMUNGEN
================================================================================

15.1 Anwendbares Recht

Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
UN-Kaufrechts. Zwingende Verbraucherschutzvorschriften des Staates,
in dem der Nutzer seinen gewöhnlichen Aufenthalt hat, bleiben unberührt.

15.2 Gerichtsstand

Ist der Nutzer Kaufmann, juristische Person des öffentlichen Rechts
oder öffentlich-rechtliches Sondervermögen, ist ausschließlicher
Gerichtsstand für alle Streitigkeiten aus diesem Vertrag der Sitz
des Betreibers. Für Verbraucher gelten die gesetzlichen
Zuständigkeitsregelungen.

15.3 Online-Streitbeilegung

Die Europäische Kommission stellt eine Plattform zur
Online-Streitbeilegung (OS) bereit, die unter
https://ec.europa.eu/consumers/odr/ erreichbar ist. Der Betreiber ist
weder bereit noch verpflichtet, an einem Streitbeilegungsverfahren vor
einer Verbraucherschlichtungsstelle teilzunehmen.

15.4 Salvatorische Klausel

Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden,
bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. Die
unwirksame Bestimmung ist durch eine wirksame zu ersetzen, die dem
wirtschaftlichen Zweck der unwirksamen Bestimmung am nächsten kommt.

15.5 Keine Abtretung

Der Nutzer ist nicht berechtigt, Rechte aus diesem Vertrag ohne
vorherige schriftliche Zustimmung des Betreibers an Dritte abzutreten.

15.6 Vollständigkeit

Diese AGB enthalten alle Vereinbarungen zwischen den Parteien
hinsichtlich des Vertragsgegenstands. Mündliche Nebenabreden bestehen
nicht.

================================================================================
                        KONTAKT
================================================================================

Bei Fragen zu diesen AGB wenden Sie sich bitte an:

[GAIA Software UG (haftungsbeschränkt)]
[Gerhardstraße 6]
[22926 Ahrensburg]

E-Mail: [kontakt@universe-app.de]

================================================================================
                        VERSIONSHISTORIE
================================================================================

Version 1.1 - 28. Januar 2026
- Ergänzung Ziffer 9.5: Blockieren von Nutzern
- Ergänzung Ziffer 9.6: Benachrichtigungseinstellungen für einzelne Chats
- Ergänzung Ziffer 10.2a: Automatische Inhaltsmoderation (Wortfilter,
  automatische Löschung bei Mehrfachmeldungen)
- Ergänzung Ziffer 12.2a: Kontolöschung mit 30-tägiger Wiederherstellungsfrist

Version 1.0 - 05. Januar 2026
- Erstveröffentlichung der Allgemeinen Geschäftsbedingungen

================================================================================
                    Ende der Allgemeinen Geschäftsbedingungen
================================================================================`;

  return (
    <div className="terms-page">
      {/* Header - Full AppHeader for authenticated, simple header for unauthenticated */}
      {isAuthenticated ? (
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        />
      ) : (
        <header className="terms-simple-header">
          <h1 className="terms-header-title">{t('profile.terms')}</h1>
        </header>
      )}

      {/* Tab Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <div className="tab-navigation-terms">
          <span className="tab-title">{t('profile.terms')}</span>
        </div>
      )}

      {/* Terms and Conditions Content */}
      <main className={`terms-content ${!isAuthenticated ? 'unauthenticated' : ''}`}>
        {/* Back Button */}
        <BackButton
          onClick={onNavigateBack}
          className="terms-back-button"
        />

        {/* Terms Text */}
        <div className="terms-text">
          <pre>{termsContent}</pre>
        </div>
      </main>

      {/* Bottom Navigation - Only for authenticated users */}
      {isAuthenticated && (
        <>
          <BottomNavigation
            activeTab="profile"
            onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
          />
          <CreateOptionsMenu
            isOpen={showCreateOptions}
            onClose={() => setShowCreateOptions(false)}
          />
        </>
      )}
    </div>
  );
}

export default TermsAndConditions;
